import { optimizeRenderBindings } from "../layout/optimize-render-bindings.js";
import type { BehaviorGraph } from "../layout/types.js";
import type { LayoutNode } from "../layout/types.js";
import {
  collectBehaviorDependencies,
  compileEventHandlers,
} from "./compileEvents.js";
import {
  compileListFilterRules,
  compileListSource,
  compileListSortRules,
} from "./compileListFilterSortRules.js";
import { compileRowVariantRules } from "./compileRowVariantRules.js";
import { indexGraph } from "./graph.js";
import type {
  CompileFlowOptions,
  CompiledBehavior,
  SceneCatalogDependencyIndex,
} from "./types.js";

export type { CompiledBehavior } from "./types.js";

/**
 * Compiles a layout node's behavior graph into value and flow handlers.
 *
 * @param behavior - Blueprint graph attached to the layout node.
 * @param ownerNodeId - Layout node id that owns the graph (patch target).
 */
export function compileBehavior(
  behavior: BehaviorGraph,
  ownerNodeId: string,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
  layoutNodeById?: Map<string, LayoutNode>,
): CompiledBehavior {
  const indexed = indexGraph(optimizeRenderBindings(behavior));

  const rowVariantRules = compileRowVariantRules(indexed, catalogDeps);
  const listSource = compileListSource(indexed, catalogDeps);
  const listFilterRules = compileListFilterRules(indexed, catalogDeps);
  const listSortRules = compileListSortRules(indexed, catalogDeps);

  const eventHandlers = compileEventHandlers(
    indexed,
    ownerNodeId,
    catalogDeps,
    flowOptions,
    layoutNodeById,
  );

  return {
    eventHandlers,
    requiresRuntimeDomTarget: eventHandlers.some(
      (handler) => handler.flow.requiresRuntimeDomTarget,
    ),
    rowVariantRules: rowVariantRules.length > 0 ? rowVariantRules : undefined,
    listSource,
    listFilterRules: listFilterRules.length > 0 ? listFilterRules : undefined,
    listSortRules: listSortRules.length > 0 ? listSortRules : undefined,
  };
}

export { collectBehaviorDependencies };
